// 普通页面的筛选器因为是dva中存储了数据，所以直接从dva中获取seletor的值，再经过处理，调用getSelectorMap获取中英文配置
// 特殊页面，当前selector是直接在页面中请求的，所以配置的值暂时沿用老版本方案，存放在全局变量nameMap和cnEnNameMap之中

const nameMap = {};
const cnEnNameMap = {};

const DL_GAME_CODES = ['dl', 'dl2'];
const DL_ACTIVE_REPORT_PATH_PREFIXES = [
  'overview/',
  'realtime/users',
  'realtime/nightingaleusers',
  'engagement/active',
  'engagement/churn',
  'engagement/return',
  'engagement/ratereturn',
  'engagement/register',
  'engagement/retention',
  'engagement/rollingretention',
  'retention/index',
];
const filteredSelectorCache = new WeakMap<object, Map<string, any>>();
const OTHER_DIST_PARTNER_PREFIX = 'other dist./partner';

export interface IPlatformOptionFilterConfig {
  enabled?: boolean;
  gameCodes?: string[];
  excludeNameEnPrefixes?: string[];
}

/** 过滤 DL/DL2 活跃报表平台选项（默认隐藏 Other Dist./Partner*） */
export const filterDlActiveReportSelector = (
  selectorData: any,
  gameCode = '',
  filterPath = '',
  config?: IPlatformOptionFilterConfig,
) => {
  const normalizedGameCode = String(gameCode || '').trim().toLowerCase();
  const normalizedPath = String(filterPath || '').trim().replace(/^\/+/, '').toLowerCase();
  const defaultPathEnabled = DL_ACTIVE_REPORT_PATH_PREFIXES
    .some(pathPrefix => normalizedPath.startsWith(pathPrefix));
  const enabled = config?.enabled ?? defaultPathEnabled;
  const normalizedGameCodes = (config?.gameCodes || DL_GAME_CODES)
    .map(code => String(code).trim().toLowerCase());
  const normalizedExcludePrefixes = (
    config?.excludeNameEnPrefixes || [OTHER_DIST_PARTNER_PREFIX]
  )
    .map(prefix => String(prefix).trim().toLowerCase())
    .filter(Boolean);
  const shouldFilter = enabled && normalizedGameCodes.includes(normalizedGameCode);

  if (!shouldFilter || !Array.isArray(selectorData?.os_list)) return selectorData;

  const cacheKey = [
    normalizedGameCode,
    normalizedPath,
    enabled,
    normalizedGameCodes.join(','),
    normalizedExcludePrefixes.join(','),
  ].join(':');
  const selectorCache = filteredSelectorCache.get(selectorData) || new Map<string, any>();
  if (selectorCache.has(cacheKey)) return selectorCache.get(cacheKey);
  if (!filteredSelectorCache.has(selectorData)) {
    filteredSelectorCache.set(selectorData, selectorCache);
  }

  const osList = selectorData.os_list.filter((item: { os_name_en?: string }) => {
    const osName = String(item?.os_name_en || '').trim().toLowerCase();
    return !normalizedExcludePrefixes.some(prefix => osName.startsWith(prefix));
  });

  const result = osList.length === selectorData.os_list.length
    ? selectorData
    : { ...selectorData, os_list: osList };
  selectorCache.set(cacheKey, result);
  return result;
};

export const buildNameMap = (list = [] as any, enKey = '', chKey = '') => {
  const map = {};
  list?.forEach((item: string | number) => {
    if (typeof item === 'string') {
      map[item] = item;
    } else {
      map[item[enKey]] = item[chKey] || item[enKey];
    }
  });
  return map;
};

export const bindCnEnNameMap = (list = [] as any, codeKey = '', enKey = '', chKey = '') => {
  const map = {};
  list?.forEach(item => {
    if (typeof item === 'string') {
      map[item] = item;
    } else {
      map[item[codeKey]] = { cn: item[chKey], en: item[enKey] };
    }
  });
  return map;
};

export const getNameMap = (gameCode: string) => nameMap[gameCode];
export const getCnEnNameMap = (gameCode: string) => cnEnNameMap[gameCode];

export const setNameMap = (gameCode: string, listData: { channel_list: any; os_list: any; zone_list: any; country_list: any; area_list: any; network_list: any; lang_list: any; }) => {
  const {
    channel_list,
    os_list,
    zone_list,
    country_list,
    area_list,
    network_list,
    lang_list
  } = listData;
  if (!nameMap[gameCode]) nameMap[gameCode] = {};
  if (!cnEnNameMap[gameCode]) cnEnNameMap[gameCode] = {};

  const countryList = (() => {
    if (country_list && Array.isArray(country_list)) return country_list;
    let result: any[] = [];
    area_list?.forEach((item: { country_set: any; }) => {
      if (item.country_set && Array.isArray(item.country_set)) {
        result = result.concat(item.country_set);
      }
    });
    return result;
  })();

  Object.assign(nameMap[gameCode], {
    channel: buildNameMap(channel_list, 'channel_name_en', 'channel_name_ch'),
    system: buildNameMap(os_list, 'os_name_en', 'os_name_ch'),
    zone: buildNameMap(zone_list, 'zone_name_en', 'zone_name_ch'),
    country: buildNameMap(countryList, 'country_name_en', 'country_name_ch'),
    region: buildNameMap(area_list, 'area_name_en', 'area_name_ch'),
    language: buildNameMap(lang_list, 'lang_name_en', 'lang_name_ch'),
    network: buildNameMap(network_list)
  });

  Object.assign(cnEnNameMap[gameCode], {
    channel: bindCnEnNameMap(channel_list, 'channel_id', 'channel_name_en', 'channel_name_ch'),
    system: bindCnEnNameMap(os_list, 'os_id', 'os_name_en', 'os_name_ch'),
    zone: bindCnEnNameMap(zone_list, 'zone_code', 'zone_name_en', 'zone_name_ch'),
    country: bindCnEnNameMap(countryList, 'country_code', 'country_name_en', 'country_name_ch'),
    region: bindCnEnNameMap(area_list, 'area_code', 'area_name_en', 'area_name_ch'),
    language: bindCnEnNameMap(lang_list, 'lang_code', 'lang_name_en', 'lang_name_ch'),
    network: bindCnEnNameMap(network_list)
  });
};

// 获取特殊页面（如：ua和竞品）的筛选器中英文配置
export const listFetchInterceptor = (game_code: string, data: { channel_list: any; os_list: any; zone_list: any; country_list: any; area_list: any; network_list: any; lang_list: any; }) => {
  if (!data) return;
  setNameMap(game_code, data);
};

// 获取普通页面的筛选器中英文配置
export const getSelectorMap = (selectorList: any) => {
  const {
    channel_list,
    os_list,
    zone_list,
    country_list,
    area_list,
    network_list,
    lang_list,
    category_list,
    churn_level_list,
    bundle_list,
    ad_format,
    ua_network
  } = selectorList;
  // if (!nameMap[gameCode]) nameMap[gameCode] = {}
  // if (!cnEnNameMap[gameCode]) cnEnNameMap[gameCode] = {}

  const countryList = (() => {
    if (country_list && Array.isArray(country_list)) return country_list;
    let result: any[] = [];
    area_list?.forEach((item: { country_set: any; }) => {
      if (item.country_set && Array.isArray(item.country_set)) {
        result = result.concat(item.country_set);
      }
    });
    return result;
  })();

  const productList = (() => {
    let result: any[] = [];
    category_list?.forEach((item: { product_set: any; }) => {
      if (item.product_set && Array.isArray(item.product_set)) {
        result = result.concat(item.product_set);
      }
    });
    return result;
  })();

  const nameEnMap = {
    channel: buildNameMap(channel_list, 'channel_name_en', 'channel_name_ch'),
    system: buildNameMap(os_list, 'os_name_en', 'os_name_ch'),
    os: buildNameMap(os_list, 'os_name_en', 'os_name_ch'),
    zone: buildNameMap(zone_list, 'zone_name_en', 'zone_name_ch'),
    country: buildNameMap(countryList, 'country_name_en', 'country_name_ch'),
    region: buildNameMap(area_list, 'area_name_en', 'area_name_ch'),
    language: buildNameMap(lang_list, 'lang_name_en', 'lang_name_ch'),
    lang: buildNameMap(lang_list, 'lang_name_en', 'lang_name_ch'),
    bundle: buildNameMap(bundle_list, 'bundle_name_en', 'bundle_name_ch'),
    network: buildNameMap(network_list),
    category: buildNameMap(category_list, 'category_name_en', 'category_name_ch'),
    product: buildNameMap(productList, 'product_name_en', 'product_name_ch'),
    churn_level: buildNameMap(churn_level_list, 'churn_level_en', 'churn_level_ch'),
    ad_format: buildNameMap(ad_format, 'name_en', 'name_ch'),
    ua_network: buildNameMap(ua_network, 'name_en', 'name_ch'),
  };

  const nameCnMap = {
    channel: bindCnEnNameMap(channel_list, 'channel_id', 'channel_name_en', 'channel_name_ch'),
    system: bindCnEnNameMap(os_list, 'os_id', 'os_name_en', 'os_name_ch'),
    os: bindCnEnNameMap(os_list, 'os_id', 'os_name_en', 'os_name_ch'),
    zone: bindCnEnNameMap(zone_list, 'zone_code', 'zone_name_en', 'zone_name_ch'),
    country: bindCnEnNameMap(countryList, 'country_code', 'country_name_en', 'country_name_ch'),
    region: bindCnEnNameMap(area_list, 'area_code', 'area_name_en', 'area_name_ch'),
    language: bindCnEnNameMap(lang_list, 'lang_code', 'lang_name_en', 'lang_name_ch'),
    lang: bindCnEnNameMap(lang_list, 'lang_code', 'lang_name_en', 'lang_name_ch'),
    bundle: bindCnEnNameMap(bundle_list, 'bundle_id', 'bundle_name_en', 'bundle_name_ch'),
    network: bindCnEnNameMap(network_list),
    category: bindCnEnNameMap(category_list, 'category_code', 'category_name_en', 'category_name_ch'),
    product: bindCnEnNameMap(productList, 'product_code', 'product_name_en', 'product_name_ch'),
    churn_level: bindCnEnNameMap(churn_level_list,'churn_level', 'churn_level_en', 'churn_level_ch'),
    ad_format: bindCnEnNameMap(ad_format, 'code', 'name_en', 'name_ch'),
    ua_network: bindCnEnNameMap(ua_network, 'code', 'name_en', 'name_ch'),
  };

  return { nameEnMap, nameCnMap };
};